/**
 * Shared SAFE page context — the single source of truth for every page/route
 * field we ever hand to Google (GA4 config, GA4 events, and the direct Google
 * Ads conversion).
 *
 * Rules:
 *   - never a raw URL: query strings and hashes are always dropped;
 *   - token-shaped and private path segments are replaced with `:id`;
 *   - the referrer is reduced to a safe same-origin path or an external origin;
 *   - the title is length-capped and rejected if it looks like contact data;
 *   - callers can never override any of these fields.
 *
 * This context is applied with a documented `gtag('set', …)` BEFORE any
 * destination is configured, so Google's own automatic collection (session
 * start, user engagement, and anything else the tag emits on its own)
 * inherits the sanitized values instead of reading the raw document URL.
 *
 * NOT rewritten (deliberately): `document.location`/`document.referrer`
 * themselves, and the click identifiers (`gclid`/`gbraid`/`wbraid`) the
 * Google online tag reads from the real URL. Rewriting those would break ad
 * click attribution. We control what WE send, not what the browser exposes.
 */

import { ensureGtag, getConfiguredDestinations, updateGoogleTagDestination } from "./google-tag";
import { isPublicTrackableRoute } from "./public-routes";

const MAX_STR = 120;
const MAX_TITLE = 100;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LONG_HEX_RE = /^[0-9a-f]{16,}$/i;
const DIGITS_RE = /^\d{6,}$/;
const TOKEN_CHARS_RE = /^[A-Za-z0-9_-]+$/;

/** Path parents whose child segment is always an opaque identifier. */
const TOKENISED_PARENTS = new Set([
  "q",
  "quote",
  "roof-quote",
  "token",
  "booking",
  "invoice",
  "confirmation",
  "r",
  "rescue",
  "unsubscribe",
]);

/**
 * Token-shaped segment detection. Deliberately conservative so real content
 * slugs ("solar-service-maintenance") survive, while random tokens
 * (base64url, hex digests, uuids, numeric ids) are redacted.
 */
export function isOpaqueSegment(seg: string): boolean {
  if (!seg) return false;
  if (UUID_RE.test(seg)) return true;
  if (LONG_HEX_RE.test(seg)) return true;
  if (DIGITS_RE.test(seg)) return true;
  if (!TOKEN_CHARS_RE.test(seg)) return false;
  const mixedCase = /[a-z]/.test(seg) && /[A-Z]/.test(seg);
  if (seg.length >= 16 && mixedCase && /\d/.test(seg)) return true;
  if (seg.length >= 24 && mixedCase) return true;
  return false;
}

/** Safe pathname: no query, no hash, opaque segments replaced with `:id`. */
export function safePagePath(pathname: string): string {
  const clean = (pathname || "/").split(/[?#]/)[0];
  const parts = clean.split("/");
  const out = parts.map((seg, i) => {
    if (!seg) return seg;
    const parent = (parts[i - 1] || "").toLowerCase();
    if (TOKENISED_PARENTS.has(parent) || isOpaqueSegment(seg)) return ":id";
    return seg;
  });
  return out.join("/").slice(0, MAX_STR) || "/";
}

/** Safe absolute page location (origin + safe path). Never query/hash. */
export function safePageLocation(pathOverride?: string): string | undefined {
  if (typeof window === "undefined") return undefined;
  const path =
    typeof pathOverride === "string" && pathOverride.startsWith("/")
      ? pathOverride
      : window.location.pathname;
  return window.location.origin + safePagePath(path);
}

/**
 * Safe referrer.
 *   - external → origin only;
 *   - same-origin PUBLIC route → origin + safe path;
 *   - same-origin private/unknown route → origin only (the path never leaves);
 *   - missing / unusable → EMPTY STRING.
 *
 * The empty string matters: an omitted `page_referrer` leaves Google reading
 * the raw `document.referrer` (or, on an SPA route change, the previous page),
 * so a rejected referrer must be sent explicitly as empty rather than skipped.
 */
export function safePageReferrer(): string {
  if (typeof document === "undefined" || typeof window === "undefined") return "";
  try {
    const r = document.referrer;
    if (!r) return "";
    const u = new URL(r);
    if (u.protocol !== "http:" && u.protocol !== "https:") return "";
    if (u.origin !== window.location.origin) return u.origin;
    const path = safePagePath(u.pathname);
    return isPublicTrackableRoute(path) ? u.origin + path : u.origin;
  } catch {
    return "";
  }
}

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;
const PHONE_RE = /(?:\+?\d[\s\-().]*){7,}/;

/** Neutral title used whenever the real one is empty or rejected. */
export const NEUTRAL_PAGE_TITLE = "Magic Builders";

/**
 * Page title, capped. An empty or contact-looking title is REPLACED with the
 * neutral title, never omitted — an omitted `page_title` lets the tag fall
 * back to the raw `document.title` of this or the previous page.
 */
export function safePageTitle(): string {
  if (typeof document === "undefined") return NEUTRAL_PAGE_TITLE;
  const t = (document.title || "").trim();
  if (!t) return NEUTRAL_PAGE_TITLE;
  if (EMAIL_RE.test(t) || PHONE_RE.test(t)) return NEUTRAL_PAGE_TITLE;
  return t.slice(0, MAX_TITLE);
}

/**
 * Standard GA4 acquisition fields, from validated `utm_*` values on the
 * CURRENT url.
 *
 * GA4 reads campaign attribution from the documented `campaign_source`,
 * `campaign_medium`, `campaign_name`, `campaign_content` and `campaign_term`
 * config/set fields — NOT from custom event parameters, and not from a
 * `page_location` we deliberately strip of its query string.
 * https://developers.google.com/analytics/devguides/collection/ga4/reference/config
 *
 * Values pass the same strict validation used for lead attribution: a
 * malformed value is dropped, never sanitized into something else, and never
 * blocks anything. No click identifiers are read here.
 */
const UTM_FIELDS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_id",
] as const;
/** Conservative: letters, digits, dot, dash, underscore, plus, space. */
const UTM_VALUE_RE = /^[A-Za-z0-9._+\- ]{1,100}$/;

/**
 * Same narrow allowance as `lead-attribution`: Google Ads ValueTrack renders
 * `{campaignid}` and `{creative}` as BARE numeric ids, which the generic
 * `PHONE_RE` guard would otherwise reject as contact data. Digits-only values
 * up to 20 characters are permitted in the three identifier fields; the human
 * label fields and the visitor's own search keyword keep the strict check, and
 * no click identifier is read here.
 */
const NUMERIC_ID_RE = /^\d{1,20}$/;
const NUMERIC_ID_FIELDS: ReadonlySet<string> = new Set([
  "utm_campaign",
  "utm_content",
  "utm_id",
]);

/**
 * Local, dependency-free UTM parser. `lead-attribution` applies the same rules
 * for the CRM copy; duplicating the few lines here keeps this module free of a
 * consent → page-context import cycle.
 */
function parseCampaignUtm(search: string): Partial<Record<(typeof UTM_FIELDS)[number], string>> {
  const out: Partial<Record<(typeof UTM_FIELDS)[number], string>> = {};
  try {
    const params = new URLSearchParams(search || "");
    for (const k of UTM_FIELDS) {
      const v = params.get(k);
      if (!v) continue;
      const trimmed = v.trim();
      if (!trimmed || trimmed.length > 100) continue;
      if (!UTM_VALUE_RE.test(trimmed)) continue;
      const numericIdOk = NUMERIC_ID_FIELDS.has(k) && NUMERIC_ID_RE.test(trimmed);
      if (!numericIdOk && (EMAIL_RE.test(trimmed) || PHONE_RE.test(trimmed))) continue;
      out[k] = trimmed;
    }
  } catch {
    /* an unusable query string simply yields no attribution */
  }
  return out;
}

export function safeCampaignFields(search?: string): Record<string, string> {
  const raw =
    typeof search === "string"
      ? search
      : typeof window !== "undefined"
        ? window.location.search
        : "";
  const utm = parseCampaignUtm(raw);
  const out: Record<string, string> = {};
  if (utm.utm_source) out.campaign_source = utm.utm_source;
  if (utm.utm_medium) out.campaign_medium = utm.utm_medium;
  if (utm.utm_campaign) out.campaign_name = utm.utm_campaign;
  if (utm.utm_content) out.campaign_content = utm.utm_content;
  if (utm.utm_term) out.campaign_term = utm.utm_term;
  // GA4's documented acquisition field for the advertiser's campaign id.
  if (utm.utm_id) out.campaign_id = utm.utm_id;
  return out;
}

export interface SafePageContext {
  page_path: string;
  page_location?: string;
  /** Always present: "" when there is no safe referrer to report. */
  page_referrer: string;
  /** Always present: the neutral title when the real one is unusable. */
  page_title: string;
}

/** The context every Google call carries. Callers may not override any of it. */
export function buildPageContext(pathOverride?: string): SafePageContext {
  const path =
    typeof pathOverride === "string" && pathOverride.startsWith("/")
      ? pathOverride
      : typeof window !== "undefined"
        ? window.location.pathname
        : "/";
  const ctx: SafePageContext = {
    page_path: safePagePath(path),
    // Explicit, never omitted — see safePageReferrer / safePageTitle.
    page_referrer: safePageReferrer(),
    page_title: safePageTitle(),
  };
  const loc = safePageLocation(path);
  if (loc) ctx.page_location = loc;
  return ctx;
}

/** Fields no caller may ever supply — routing and page context are ours. */
export const CONTEXT_RESERVED_KEYS: ReadonlySet<string> = new Set([
  "page_path",
  "page_location",
  "page_referrer",
  "page_title",
  // Acquisition fields are derived from the validated url, never from a caller.
  "campaign_source",
  "campaign_medium",
  "campaign_name",
  "campaign_content",
  "campaign_term",
  "campaign_id",
  "campaign",
]);

/**
 * Apply the shared safe context as gtag defaults. Call BEFORE configuring any
 * destination, and again on every route change, so automatic hits inherit it.
 */
export function applySharedPageContext(pathOverride?: string): SafePageContext | null {
  if (typeof window === "undefined") return null;
  const gtag = ensureGtag();
  if (!gtag) return null;
  const ctx = buildPageContext(pathOverride);
  const campaign = safeCampaignFields();
  try {
    gtag("set", { ...ctx, ...campaign });
  } catch {
    /* noop */
  }
  // A `set` alone does not reach a destination that was configured earlier in
  // the page's life: on an SPA route change its automatic events (scroll,
  // video, user_engagement — the collectors that remain enabled) would keep
  // the PREVIOUS page's fields. Re-send each configured destination's config
  // with `update: true` and `send_page_view: false`, which refreshes the
  // fields without generating an extra page view.
  for (const id of getConfiguredDestinations()) {
    try {
      updateGoogleTagDestination(id, { ...ctx, ...campaign });
    } catch {
      /* noop */
    }
  }
  return ctx;
}
